import React, {ReactElement, ReactNode, useState, useCallback, useEffect, useMemo} from 'react';
import {Breadcrumbs, makeStyles, Typography} from '@material-ui/core';
import {default as MuiLink} from '@material-ui/core/Link';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import {useTranslation} from 'react-i18next';
import {Link, useHistory} from 'react-router-dom';
import {USER_HOME} from 'shared/routing/paths';
import BreadcrumbContext from './BreadcrumbContext';
import createBreadcrumb from './createBreadcrumb';
import {Breadcrumb} from './types';

type Props = {
  children: ReactNode;
};

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2)
  }
}));

const initialState: Breadcrumb[] = [];

export default function BreadcrumbContextProvider({children}: Props): ReactElement {
  const classes = useStyles();
  const {t} = useTranslation('common');
  const history = useHistory();
  const homeBreadcrumb = useMemo(() => createBreadcrumb(t('common:breadcrumbs.home'), USER_HOME), [
    t
  ]);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(initialState);

  const updateBreadcrumbs = useCallback(
    function(breadcrumbs: Breadcrumb[]) {
      setBreadcrumbs([homeBreadcrumb, ...breadcrumbs]);
    },
    [homeBreadcrumb]
  );

  useEffect(() => {
    return history.listen(function() {
      if (breadcrumbs.length > 1) {
        setBreadcrumbs(initialState);
      }
    });
  }, [breadcrumbs.length, history, updateBreadcrumbs]);

  return (
    <BreadcrumbContext.Provider value={updateBreadcrumbs}>
      <Breadcrumbs
        maxItems={3}
        separator={<KeyboardArrowRightIcon color="secondary" fontSize="small" />}
        aria-label="breadcrumb"
        className={classes.root}
      >
        {breadcrumbs.map(breadcrumb =>
          breadcrumb.to ? (
            <MuiLink key={breadcrumb.to} component={Link} color="inherit" to={breadcrumb.to}>
              {breadcrumb.label}
            </MuiLink>
          ) : (
            <Typography key={breadcrumb.label} color="textPrimary">
              {breadcrumb.label}
            </Typography>
          )
        )}
      </Breadcrumbs>
      {children}
    </BreadcrumbContext.Provider>
  );
}
